// =============================================================================
// AGENT 2 — MISTRZ UI: LICZNIK CZASU
// Wywołuje onTimeUp() gdy timer dobiegnie końca — logika jest w Agent 3/1.
// =============================================================================

import { useEffect, useRef } from 'react';
import './Timer.css';
import { useGame } from '../../store/gameStore';
import { GAME_DURATION_SECONDS } from '../../store/constants';
import { deriveTempoStage, isFinalCountdown } from '../../store/tempoStage';

interface TimerProps {
  onTimeUp: () => void;
}

const STAGE_LABEL: Record<ReturnType<typeof deriveTempoStage>, string> = {
  early: 'Tempo: rozruch',
  mid: 'Tempo: presja rośnie',
  final: 'Tempo: finał',
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function Timer({ onTimeUp }: TimerProps) {
  const { state, tick } = useGame();
  const onTimeUpRef = useRef(onTimeUp);
  onTimeUpRef.current = onTimeUp;

  useEffect(() => {
    if (state.phase !== 'playing') return;

    const interval = setInterval(() => {
      tick();
    }, 1000);

    return () => clearInterval(interval);
  }, [state.phase, tick]);

  useEffect(() => {
    if (state.timeLeft === 0 && state.phase === 'playing') {
      onTimeUpRef.current();
    }
  }, [state.timeLeft, state.phase]);

  const stage = deriveTempoStage(state.timeLeft);
  const showFinalAlert = isFinalCountdown(state.timeLeft);
  const fillPct = (state.timeLeft / GAME_DURATION_SECONDS) * 100;
  const fillColor = stage === 'early' ? 'var(--color-success)'
    : stage === 'mid' ? 'var(--color-warning)'
    : 'var(--color-danger)';

  return (
    <div
      className={`timer${showFinalAlert ? ' timer--final-alert' : ''}`}
      role="timer"
      aria-label={`Pozostały czas: ${formatTime(state.timeLeft)}`}
      data-stage={stage}
    >
      <div className="timer__stage" aria-live="polite">{STAGE_LABEL[stage]}</div>
      <div className="timer__display" data-stage={stage}>
        {formatTime(state.timeLeft)}
      </div>
      <div className="timer__bar" aria-hidden>
        <div
          className="timer__bar-fill"
          style={{ width: `${fillPct}%`, backgroundColor: fillColor }}
        />
      </div>
    </div>
  );
}
